/**
 * Created by Arif Topcu in 2019
 */

import React, { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "react-query";
import styles from "./FilterSelection.module.css";
import Combobox, { Size } from "@ls/react-combobox-grid";
import Radio from "@ls/react-radio-switch";
import Grid from "@ls/react-grid";
import Button from "@ls/react-button";
import Icon from "react-icons-kit";
import { ic_arrow_forward } from "react-icons-kit/md/ic_arrow_forward";
import { ic_arrow_back } from "react-icons-kit/md/ic_arrow_back";
import PivotFilterDefinition from "state/pivots/PivotFilterDefinition";
import {
  selectFetchingGroupingLevels,
  selectGroupingLevelOptions,
  selectAsOfDateValue,
} from "state/selections/selector";
import { useSelector } from "react-redux";
import { exchange } from "@ls/js-fetch";
import SelectionState from "state/selections/SelectionState";

const radioOptions = [
  {
    label: "Include",
    value: "INCLUDE",
  },
  {
    label: "Exclude",
    value: "EXCLUDE",
  },
];
const columnDefs = [
  {
    field: "value",
  },
];
const FilterSelection = ({ definition, setFiltersActive, setNewFilter }) => {
  const groupingLevelOptions = useSelector(selectGroupingLevelOptions);
  const fetchingGroupingLevels = useSelector(selectFetchingGroupingLevels);

  const asOfDateValue = useSelector(
    selectAsOfDateValue(SelectionState.UNCOMMITTED)
  );

  const [selectedLevel, setSelectedLevel] = useState(definition.levels);
  const [mainGridSelected, setMainGridSelected] = useState([]);
  const [secondGridSelected, setSecondGridSelected] = useState([]);
  const [includeExclude, setIncludeExclude] = useState(radioOptions[0].value);
  const [secondGridRowData, setSecondGridRowData] = useState([]);
  const url = process.env.REACT_APP_BASE_URL_GROUPING_SERVICE;

  const filterValuesQuery = useQuery({
    enabled: !!asOfDateValue && !!selectedLevel,
    queryKey: [asOfDateValue, selectedLevel],
    queryFn: async () => {
      const { data } = await exchange({
        baseURL: url,
        url: "keys",
        params: {
          date: asOfDateValue,
          group_type: "entity_list",
          ids: 559730,
          levels: selectedLevel,
          appName: process.env.REACT_APP_ID,
        },
      });
      return data?.[selectedLevel]?.filter(({ value }) => value);
    },
  });

  const onChangeGroupingLevelOptions = useCallback(
    (level) => {
      setSelectedLevel(level);
      setMainGridSelected([]);
      setSecondGridSelected([]);
      setSecondGridRowData([]);
    },
    [
      setSelectedLevel,
      setMainGridSelected,
      setSecondGridSelected,
      setSecondGridRowData,
    ]
  );

  const onSelectLeftGridRows = useCallback(
    (rows) => {
      setMainGridSelected(rows);
    },
    [setMainGridSelected]
  );

  return (
    <>
      <div className={styles.mask} />
      <div className={styles.root}>
        <div className={styles.top}>
          <div className={styles.levelSelector}>
            <Combobox
              options={groupingLevelOptions}
              onChange={onChangeGroupingLevelOptions}
              value={selectedLevel}
              isLoading={fetchingGroupingLevels}
              label="Pivot Levels: "
              variation={0}
              gridHeight={250}
              size={Size.S}
            />
          </div>
          <div className={styles.radio}>
            <Radio
              options={radioOptions}
              onChange={setIncludeExclude}
              value={includeExclude}
              variation={0}
            />
          </div>
        </div>
        <div className={styles.grids}>
          <div className={styles.grid}>
            <Grid
              rowData={filterValuesQuery.data}
              loadingMessage={
                filterValuesQuery.isFetching ? "Loading..." : undefined
              }
              headerHeight={0}
              rowSelection="multiple"
              columnDefs={columnDefs}
              onSelectionChanged={({ api }) =>
                onSelectLeftGridRows(api.getSelectedRows())
              }
              sideBar={null}
            />
          </div>
          <div className={styles.divider}>
            <Button
              onButtonClick={() => {
                const unique = new Set([
                  ...secondGridRowData,
                  ...mainGridSelected,
                ]);
                setSecondGridRowData([...unique]);
              }}
              icon={<Icon icon={ic_arrow_forward} />}
              iconColor="#1A3B73"
              variation={5}
            />
            <Button
              onButtonClick={() => {
                setSecondGridRowData(
                  secondGridRowData.filter(
                    ({ value }) =>
                      !secondGridSelected.find(
                        (selected) => selected.value === value
                      )
                  )
                );
              }}
              icon={<Icon icon={ic_arrow_back} />}
              iconColor="#1A3B73"
              variation={5}
            />
          </div>
          <div className={styles.grid}>
            <Grid
              rowData={secondGridRowData}
              headerHeight={0}
              rowSelection="multiple"
              columnDefs={columnDefs}
              onSelectionChanged={({ api }) =>
                setSecondGridSelected(api.getSelectedRows())
              }
              sideBar={null}
            />
          </div>
        </div>
        <div className={styles.bottom}>
          <div className={styles.bottomButton}>
            <Button
              label="Cancel"
              onButtonClick={() => {
                setFiltersActive(false);
              }}
            />
          </div>
          <div className={styles.bottomButton}>
            <Button
              label="OK"
              variation={3}
              onButtonClick={() => {
                setFiltersActive(false);
                setNewFilter(
                  new PivotFilterDefinition({
                    level: selectedLevel,
                    values: secondGridRowData.map(({ value }) => value),
                    includeExclude,
                  })
                );
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

FilterSelection.propTypes = {
  definition: PropTypes.object.isRequired,
  setFiltersActive: PropTypes.func.isRequired,
};

export default memo(FilterSelection);
